import React, { ComponentProps, useCallback } from 'react'

import { useAppDispatch } from 'hooks/reduxHooks'
import { addArrivalDetails, addToCheckoutCart } from 'actions/CheckoutActions'
import { navigateToCheckout } from 'checkout/actions/navigateActions'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import OfferCheckoutButton from './OfferCheckoutButton'
import { isFlightItem, isVillaItem } from 'lib/checkout/checkoutUtils'
import { SetOptional } from 'type-fest'
import { addToCart } from 'analytics/snowplow/events'
import * as Analytics from 'analytics/analytics'
import TextButton from 'LuxKit/Button/TextButton'

// Props we want to expose from the OfferCheckoutButton
type OfferCheckoutButtonProps = Pick<ComponentProps<typeof OfferCheckoutButton>,
  'packageView'
  | 'overrideRooms'
  | 'onSubmit'
  | 'upgradeEnabled'
>
// We generally want all TextButton props available, though the signature for onSubmit should come from CheckoutButtonProps
type TextButtonProps = SetOptional<Omit<ComponentProps<typeof TextButton>, 'onSubmit'>, 'kind'>

interface Props extends OfferCheckoutButtonProps, TextButtonProps {
  addToCartAndKeepBrowsing?: boolean;
}

function OfferBookButton(props: Props) {
  const {
    children,
    packageView,
    addToCartAndKeepBrowsing,
    onSubmit,
    kind = 'primary',
    overrideRooms,
    ...rest
  } = props

  const dispatch = useAppDispatch()

  const onProceedToBooking = useCallback((items: Array<App.Checkout.AnyItem>) => {
    const villaItems = items.filter(isVillaItem)
    const flights = items.some(isFlightItem)
    if (villaItems.length > 0) {
      villaItems.forEach(item => {
        dispatch(addArrivalDetails(item.itemId, { arrivalDate: item.checkIn, arrivalTime: '', arrivalFlightNumber: '' }))
      })
    }

    dispatch(addToCheckoutCart(items))

    if (addToCartAndKeepBrowsing) {
      showSnackbar('Your item has been added to the cart.', 'success', { action: { label: 'View cart', to: '/cart' } })
      return
    }

    if (flights) {
      dispatch(navigateToCheckout({ skipSpoofedModal: true }))
    } else {
      dispatch(navigateToCheckout())
    }
    Analytics.trackEvent(addToCart())
  }, [addToCartAndKeepBrowsing, dispatch])

  return (
    <OfferCheckoutButton
      {...rest}
      kind={kind}
      onSubmit={onSubmit}
      overrideRooms={overrideRooms}
      packageView={packageView}
      onProceedToBooking={onProceedToBooking}
    >
      {children}
    </OfferCheckoutButton>
  )
}

export default OfferBookButton
